import { useState, useMemo } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { KpiStrip } from '@/components/common/KpiStrip';
import { ExportMenu } from '@/components/common/ExportMenu';
import { PageLoader } from '@/components/common/LoadingSpinner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  ChevronsUpDown,
  ChevronUp,
  ChevronDown,
  Search,
  Boxes,
} from '@/utils/icons';
import { TablePagination } from '@/components/common/TablePagination';
import { useServicePageSummary, useServicesList } from '@/hooks/useServices';
import {
  createBrandedPdf,
  addMetricRow,
  addSectionHeading,
  addBrandedTable,
  downloadPdf,
  loadLogoForPdf,
  exportFilename,
} from '@/utils/pdf';
import { downloadCsv, formatExportDate } from '@/utils/csv';
import { renderPieLabel, renderActiveShape } from '@/utils/calculations';
import { PIE_COLORS, BAR_COLORS } from '@/utils/chart-colors';
import { FilterChip } from '@/components/common/FilterChip';

const HEALTH_FILTERS = [
  { value: '', label: 'All' },
  { value: 'HEALTHY', label: 'Healthy' },
  { value: 'UNHEALTHY', label: 'Unhealthy' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'TERMINATED', label: 'Terminated' },
  { value: 'RESTARTING', label: 'Restarting' },
];

const TYPE_FILTERS = [
  { value: '', label: 'All' },
  { value: 'monitoring', label: 'Monitoring' },
  { value: 'networking', label: 'Networking' },
  { value: 'security', label: 'Security' },
  { value: 'storage', label: 'Storage' },
  { value: 'platform', label: 'Platform' },
  { value: 'application', label: 'Application' },
];

function getHealthLabel(healthStatus: string | null, status: string | null): string {
  const hs = healthStatus?.toUpperCase();
  const st = status?.toUpperCase();
  if (st === 'TERMINATED') return 'Terminated';
  if (st === 'RESTARTING' || hs === 'RESTARTING') return 'Restarting';
  if (hs === 'HEALTHY' || st === 'RUNNING' || st === 'HEALTHY') return 'Healthy';
  if (hs === 'UNHEALTHY' || hs === 'ERROR' || st === 'UNHEALTHY' || st === 'ERROR') return 'Unhealthy';
  if (hs === 'PENDING' || st === 'PENDING') return 'Pending';
  if (st && !hs) return 'No Health Check';
  return 'Unknown';
}

function getHealthColor(label: string): string {
  switch (label) {
    case 'Healthy': return 'dls-text-status-online';
    case 'Unhealthy': return 'dls-text-status-offline';
    case 'Pending': return 'dls-text-status-warning';
    case 'Terminated': return 'dls-text-muted-foreground';
    case 'Restarting': return 'dls-text-status-info';
    default: return 'dls-text-muted-foreground';
  }
}

type SortKey = 'name' | 'nodeName' | 'healthStatus' | 'totalRestartCount' | 'serviceType' | 'startTime';

export function Services() {
  const { summary, loading: summaryLoading } = useServicePageSummary();

  // Chart hover state
  const [activeHealthIndex, setActiveHealthIndex] = useState<number | undefined>(undefined);

  // Table filters
  const [healthFilter, setHealthFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortKey | ''>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const limit = 25;

  const { services, total, totalPages, loading: listLoading } = useServicesList({
    health: healthFilter || undefined,
    search: search || undefined,
    serviceType: typeFilter || undefined,
    page,
    limit,
    sortBy: sortBy || undefined,
    sortOrder,
  });

  // Derived chart data
  const healthDistribution = useMemo(() => {
    if (!summary) return [];
    return [
      { name: 'Healthy', value: summary.healthy },
      { name: 'Unhealthy', value: summary.unhealthy },
      { name: 'Pending', value: summary.pending },
      { name: 'Terminated', value: summary.terminated },
      { name: 'Restarting', value: summary.restarting },
      { name: 'Unknown', value: summary.unknown },
      { name: 'No Health Check', value: summary.noHealthCheck },
    ].filter((d) => d.value > 0);
  }, [summary]);

  const servicesPerNode = useMemo(
    () => (summary?.servicesPerNode ?? []).slice(0, 15),
    [summary]
  );

  // Active filter chips
  const hasFilter = healthFilter || typeFilter || search;

  const clearFilters = () => {
    setHealthFilter('');
    setTypeFilter('');
    setSearch('');
    setPage(1);
  };

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const renderSortIcon = (key: SortKey) => {
    if (sortBy !== key) return <ChevronsUpDown className="dls-h-3.5 dls-w-3.5 dls-text-muted-foreground/50" />;
    return sortOrder === 'asc'
      ? <ChevronUp className="dls-h-3.5 dls-w-3.5" />
      : <ChevronDown className="dls-h-3.5 dls-w-3.5" />;
  };

  const handleExportPdf = async () => {
    if (!summary) return;

    const logoData = await loadLogoForPdf();
    const layout = createBrandedPdf({
      title: 'Services Report',
      subtitle: `${summary.total} services`,
      logoData,
    });

    let y = addMetricRow(
      layout.doc,
      [
        { label: 'Total Services', value: String(summary.total) },
        { label: 'Healthy', value: String(summary.healthy) },
        { label: 'Unhealthy', value: String(summary.unhealthy) },
        { label: 'Pending', value: String(summary.pending) },
        { label: 'Terminated', value: String(summary.terminated) },
        { label: 'Restarting', value: String(summary.restarting) },
      ],
      layout.startY,
      layout
    );

    if (services.length > 0) {
      y = addSectionHeading(layout.doc, 'Service List', y + 6, layout);
      addBrandedTable(
        layout.doc,
        ['Service', 'Node', 'Health', 'Restarts', 'Hostname', 'Start Time'],
        services.map((s) => [
          s.displayName,
          s.nodeName,
          getHealthLabel(s.healthStatus, s.status),
          s.totalRestartCount,
          s.hostname || '',
          formatExportDate(s.startTime),
        ]),
        y,
        layout
      );
    }

    downloadPdf(layout.doc, exportFilename('services', 'pdf'));
  };

  const handleExportCsv = () => {
    if (!services.length) return;

    downloadCsv(
      services.map((s) => ({
        Service: s.displayName,
        'Spec Name': s.specName || '',
        Node: s.nodeName,
        Portfolio: s.nodePortfolio || 'Unassigned',
        Type: s.serviceType || '',
        Health: getHealthLabel(s.healthStatus, s.status),
        'Health Status': s.healthStatus || '',
        Status: s.status || '',
        'Status Reason': s.statusReason || '',
        Restarts: s.totalRestartCount,
        Hostname: s.hostname || '',
        'Running Containers': s.runningContainers || '',
        'Start Time': formatExportDate(s.startTime),
        'Last Updated': formatExportDate(s.updatedAt),
      })),
      exportFilename('services', 'csv'),
      { report: 'Services' }
    );
  };

  if (summaryLoading) {
    return <PageLoader />;
  }

  return (
    <div className="dls-space-y-6">
      {/* Header */}
      <div className="dls-flex dls-items-center dls-justify-between dls-flex-wrap dls-gap-4">
        <div>
          <h1 className="dls-page-title dls-flex dls-items-center dls-gap-2"><Boxes className="dls-h-7 dls-w-7 dls-text-neeve-primary" />Services</h1>
          <p className="dls-text-muted-foreground">
            Service health, containers, and deployment status across nodes
          </p>
        </div>
        <ExportMenu
          onExportPdf={handleExportPdf}
          onExportCsv={handleExportCsv}
          label="Export Services"
        />
      </div>

      {/* KPI Strip */}
      {summary && (
        <KpiStrip
          items={[
            { label: 'Total Services', value: summary.total, tooltip: 'Total number of services deployed across all nodes.' },
            { label: 'Healthy', value: summary.healthy, variant: 'success' },
            { label: 'Unhealthy', value: summary.unhealthy, variant: summary.unhealthy > 0 ? 'danger' : 'default' },
            { label: 'Pending', value: summary.pending },
            { label: 'Terminated', value: summary.terminated },
            { label: 'Restarting', value: summary.restarting },
          ]}
        />
      )}

      {/* Charts */}
      <div className="dls-grid dls-gap-4 md:dls-grid-cols-2">
        {/* Health Distribution */}
        {healthDistribution.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="dls-text-base">Health Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="dls-h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={healthDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                      label={renderPieLabel}
                      activeIndex={activeHealthIndex}
                      activeShape={renderActiveShape}
                      onMouseEnter={(_, index) => setActiveHealthIndex(index)}
                      onMouseLeave={() => setActiveHealthIndex(undefined)}
                    >
                      {healthDistribution.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Services per Node */}
        {servicesPerNode.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="dls-text-base">Services per Node (Top 15)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="dls-h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={servicesPerNode} layout="vertical" margin={{ left: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis
                      type="category"
                      dataKey="nodeName"
                      width={75}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip />
                    <Bar dataKey="count" fill={BAR_COLORS[0]} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Filter chips */}
      {hasFilter && (
        <div className="dls-flex dls-items-center dls-gap-2 dls-flex-wrap">
          {healthFilter && (
            <FilterChip label={`Health: ${healthFilter}`} onRemove={() => { setHealthFilter(''); setPage(1); }} />
          )}
          {typeFilter && (
            <FilterChip label={`Type: ${typeFilter}`} onRemove={() => { setTypeFilter(''); setPage(1); }} />
          )}
          {search && (
            <FilterChip label={`Search: ${search}`} onRemove={() => { setSearch(''); setPage(1); }} />
          )}
          <button className="dls-text-xs dls-text-muted-foreground hover:dls-underline" onClick={clearFilters}>
            Clear all
          </button>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="dls-p-0 dls-overflow-x-auto">
          <Table className="dls-min-w-[800px]">
            <TableHeader>
              <TableRow>
                <TableHead>
                  <button className="dls-flex dls-items-center dls-gap-1" onClick={() => handleSort('name')}>
                    Service {renderSortIcon('name')}
                  </button>
                </TableHead>
                <TableHead>
                  <button className="dls-flex dls-items-center dls-gap-1" onClick={() => handleSort('nodeName')}>
                    Node {renderSortIcon('nodeName')}
                  </button>
                </TableHead>
                <TableHead>
                  <div className="dls-flex dls-items-center dls-gap-1">
                    Type
                    <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v === 'all' ? '' : v); setPage(1); }}>
                      <SelectTrigger className="dls-border-0 dls-h-6 dls-w-6 dls-p-0 dls-shadow-none">
                        <span />
                      </SelectTrigger>
                      <SelectContent>
                        {TYPE_FILTERS.map((f) => (
                          <SelectItem key={f.value || 'all'} value={f.value || 'all'}>
                            {f.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </TableHead>
                <TableHead>
                  <div className="dls-flex dls-items-center dls-gap-1">
                    Health
                    <Select value={healthFilter} onValueChange={(v) => { setHealthFilter(v === 'all' ? '' : v); setPage(1); }}>
                      <SelectTrigger className="dls-border-0 dls-h-6 dls-w-6 dls-p-0 dls-shadow-none">
                        <span />
                      </SelectTrigger>
                      <SelectContent>
                        {HEALTH_FILTERS.map((f) => (
                          <SelectItem key={f.value || 'all'} value={f.value || 'all'}>
                            {f.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </TableHead>
                <TableHead>
                  <button className="dls-flex dls-items-center dls-gap-1" onClick={() => handleSort('totalRestartCount')}>
                    Restarts {renderSortIcon('totalRestartCount')}
                  </button>
                </TableHead>
                <TableHead>Hostname</TableHead>
                <TableHead>
                  <button className="dls-flex dls-items-center dls-gap-1" onClick={() => handleSort('startTime')}>
                    Start Time {renderSortIcon('startTime')}
                  </button>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {listLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="dls-text-center dls-py-12 dls-text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : services.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="dls-text-center dls-py-12 dls-text-muted-foreground">
                    No services found
                  </TableCell>
                </TableRow>
              ) : (
                services.map((s) => {
                  const healthLabel = getHealthLabel(s.healthStatus, s.status);
                  return (
                    <TableRow key={`${s.nodeId}-${s.id}`}>
                      <TableCell className="dls-font-medium dls-max-w-[200px] dls-truncate" title={s.name}>
                        {s.displayName}
                      </TableCell>
                      <TableCell className="dls-max-w-[150px] dls-truncate" title={s.nodeName}>
                        {s.nodeName}
                      </TableCell>
                      <TableCell>
                        {s.serviceType ? (
                          <Badge variant="secondary" className="dls-text-xs dls-capitalize">
                            {s.serviceType}
                          </Badge>
                        ) : (
                          <span className="dls-text-xs dls-text-muted-foreground">–</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={cn('dls-font-medium', getHealthColor(healthLabel))}>
                          {healthLabel}
                        </span>
                        {s.statusReason && (
                          <p className="dls-text-xs dls-text-muted-foreground dls-truncate dls-max-w-[150px]" title={s.statusReason}>
                            {s.statusReason}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className={cn(s.totalRestartCount > 0 && 'dls-text-status-warning dls-font-medium')}>
                        {s.totalRestartCount}
                      </TableCell>
                      <TableCell className="dls-text-xs dls-text-muted-foreground dls-max-w-[120px] dls-truncate">
                        {s.hostname || '–'}
                      </TableCell>
                      <TableCell className="dls-text-xs dls-text-muted-foreground dls-whitespace-nowrap">
                        {s.startTime ? new Date(s.startTime).toLocaleString() : '–'}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          <TablePagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={limit}
            onPageChange={setPage}
          />
        </CardContent>
      </Card>

      {/* Search bar */}
      <div className="dls-flex dls-justify-center">
        <div className="dls-relative dls-w-full sm:dls-w-64">
          <Search className="dls-absolute dls-left-3 dls-top-1/2 -dls-translate-y-1/2 dls-h-4 dls-w-4 dls-text-muted-foreground" />
          <Input
            placeholder="Search services..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="dls-pl-9 dls-rounded-xl"
          />
        </div>
      </div>
    </div>
  );
}
